// src/Pages/Purchase/Vendors.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Vendors.css";

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // ← NEW: Search state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const userId =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  const fetchVendors = async () => {
    if (!userId) {
      setError("Please log in again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${backendUrl}/api/vendors/get?userId=${userId}`);
      const data = await res.json();

      if (data.success) {
        setVendors(data.data || []);
      } else {
        setError(data.error || "Failed to load vendors");
      }
    } catch (err) {
      setError("Network error. Please check your connection.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [userId]);

  useEffect(() => {
    window.addEventListener("focus", fetchVendors);
    return () => window.removeEventListener("focus", fetchVendors);
  }, []);

  // ← NEW: Filter vendors based on search
  const filteredVendors = vendors.filter((vendor) => {
    const fullName =
      [vendor.firstName, vendor.lastName].filter(Boolean).join(" ").trim() ||
      vendor.displayName ||
      vendor.name ||
      "";

    return (
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.companyName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (vendor.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.workPhone || vendor.phone || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  });

  const formatAmount = (amount) => {
    return (
      "₹" +
      Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const getFullName = (vendor) => {
    return (
      [vendor.firstName, vendor.lastName].filter(Boolean).join(" ").trim() ||
      vendor.displayName ||
      vendor.name ||
      "Unnamed Vendor"
    );
  };

  if (loading) {
    return (
      <div className="vendors-wrapper">
        <div className="vendors-container">
          <div className="state-card">
            <p>Loading vendors...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vendors-wrapper">
      <div className="vendors-container">
        {/* Header with Search */}
        <div className="vendors-header">
          <h1>Active Vendors ({filteredVendors.length})</h1>
          <div className="header-actions">
            <input
              type="text"
              placeholder="Search by name, company, email or phone..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Link to="/purchase/vendors/new">
              <button className="new-vendor-btn">
                <span>+</span> New Vendor
              </button>
            </Link>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="state-card error">
            <p>{error}</p>
          </div>
        )}

        {/* Empty State */}
        {filteredVendors.length === 0 && !loading && (
          <div className="state-card empty">
            <h3>{searchTerm ? "No vendors found" : "No Vendors Yet"}</h3>
            <p>
              {searchTerm
                ? `No match for "${searchTerm}"`
                : "Add your first vendor to get started."}
            </p>
            <Link to="/purchase/vendors/new">
              <button className="new-vendor-btn">+ Add Vendor</button>
            </Link>
          </div>
        )}

        {/* Vendors List */}
        {filteredVendors.length > 0 && (
          <div className="vendors-list">
            {/* Desktop Header */}
            <div className="table-header">
              <span></span>
              <span>NAME</span>
              <span>COMPANY</span>
              <span>EMAIL</span>
              <span>PHONE</span>
              <span>PAYABLES</span>
              <span>UNUSED CREDITS</span>
            </div>

            {/* Rows */}
            {filteredVendors.map((vendor) => (
              <div
                key={vendor._id}
                className="vendor-row"
                onClick={() => navigate(`/purchase/vendors/${vendor._id}`)}
              >
                <input type="checkbox" onClick={(e) => e.stopPropagation()} />

                <span data-label="Name" className="vendor-name">
                  <Link
                    to={`/purchase/vendors/${vendor._id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="name-link"
                  >
                    {getFullName(vendor)}
                  </Link>
                </span>

                <span data-label="Company">{vendor.companyName || "-"}</span>
                <span data-label="Email">{vendor.email || "-"}</span>
                <span data-label="Phone">
                  {vendor.workPhone || vendor.phone || "-"}
                </span>
                <span data-label="Payables" className="amount">
                  {formatAmount(vendor.payables)}
                </span>
                <span data-label="Credits" className="amount">
                  {formatAmount(vendor.unusedCredits)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Vendors;
